import React from 'react';
import { Link } from 'react-router-dom';

const FEATURE1 = 'https://images.unsplash.com/photo-1506097425191-7ad538b29cef?auto=format&fit=crop&w=900&q=80';
const FEATURE2 = 'https://images.unsplash.com/photo-1569629743817-70d8db6c323b?auto=format&fit=crop&w=900&q=80';
const FEATURE3 = 'https://images.unsplash.com/photo-1556388158-158ea5ccacbd?auto=format&fit=crop&w=900&q=80';
const PROOF = 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&w=1400&q=80';

export default function Landing() {
  return (
    <div className="landing">
      <header className="landing-nav">
        <div className="brand"><img src="/cnerzy-logo.png" alt="cnerzy" className="brand-logo" /><span>Rivet</span></div>
        <nav>
          <a href="#features">Features</a>
          <a href="#how">How it works</a>
          <a href="#pricing">Pricing</a>
        </nav>
        <div className="cta-row">
          <Link to="/login" className="btn btn-ghost">Sign in</Link>
          <Link to="/signup" className="btn btn-accent">Start free</Link>
        </div>
      </header>

      <section className="hero" style={{ background: '#000' }}>
        <div className="hero-inner">
          <div className="kicker light">Aerospace Standard Parts · RFQ Intelligence</div>
          <div className="hero-accent-rule" />
          <h1>Turn the email swamp into a defensible decision.</h1>
          <p>
            Rivet structures your aerospace standard-parts RFQs end-to-end: QPL compliance,
            approved manufacturers, 8130-3 / EASA Form 1 CoC, MOQ-aware cost, alternates,
            lead-time confidence. A decision matrix your auditor won't argue with.
          </p>
          <div className="cta-row">
            <Link to="/signup" className="btn btn-accent btn-xl">Start free — 30 RFQs</Link>
            <Link to="/login" className="btn btn-secondary btn-xl" style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}>Sign in</Link>
          </div>
          <div className="trust">Built for AS9120 distributors · SOC 2 roadmap · No ITAR in Phase 1</div>
        </div>
      </section>

      <section className="landing-section" id="features">
        <div className="section-head">
          <div className="kicker">Phase 1 capability</div>
          <h2>Structure first. Visibility second. Decision support third.</h2>
          <p>No AI hand-waving. Rivet replaces the spreadsheet without creating a new source of truth to babysit.</p>
        </div>
        <div className="feature-grid">
          <article className="feature">
            <div className="feature-img" style={{ backgroundImage: `url(${FEATURE1})` }} />
            <h3>Mailbox → structured RFQ</h3>
            <p>
              Inbound customer emails land in Rivet's mailbox. One click converts a message
              into a structured RFQ — line items, QPL, CoC, condition and all.
            </p>
          </article>
          <article className="feature">
            <div className="feature-img" style={{ backgroundImage: `url(${FEATURE2})` }} />
            <h3>Quality-aware comparison</h3>
            <p>
              Three-layer matrix surfaces every QPL, CoC, manufacturer, condition, MOQ and
              lead-time issue <em>before</em> the PO goes out. Color-and-icon, not just color.
            </p>
          </article>
          <article className="feature">
            <div className="feature-img" style={{ backgroundImage: `url(${FEATURE3})` }} />
            <h3>Audit trail that holds</h3>
            <p>
              Decisions are immutable. Every commit writes a SHA-256-hashed log with the
              reason you picked the supplier and the risks you accepted. AS9120-ready.
            </p>
          </article>
        </div>
      </section>

      <section className="landing-section alt" id="how">
        <div className="section-head">
          <div className="kicker">Flow</div>
          <h2>Five minutes from email to quote.</h2>
        </div>
        <ol className="steps">
          <li><b>Mailbox</b> — customer emails arrive; match to a customer.</li>
          <li><b>Structure</b> — convert email + Excel into a clean line-item RFQ.</li>
          <li><b>Dispatch</b> — pick suppliers; Rivet records who you contacted and when.</li>
          <li><b>Capture</b> — paste supplier quotes; quality status derives live.</li>
          <li><b>Compare</b> — three-layer matrix; recommended single or line-by-line split.</li>
          <li><b>Decide</b> — commit with reason + risks; immutable audit entry.</li>
          <li><b>Quote customer</b> — Direct/EXW or CNERZY margin; PDF + CSV export.</li>
        </ol>
      </section>

      <section className="landing-proof" style={{ backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.6), rgba(0,0,0,0.75)), url(${PROOF})` }}>
        <div>
          <div className="kicker light">Why now</div>
          <h2>AS9120 audits increasingly demand decision rationale.</h2>
          <p>
            Rivet isn't a marketplace. It isn't an ERP. It's the missing middle — the
            structured-data backbone aerospace distributors need to ship faster, reduce
            rework, and defend every supplier selection.
          </p>
        </div>
      </section>

      <section className="landing-section" id="pricing">
        <div className="section-head">
          <div className="kicker">Pricing</div>
          <h2>Priced to displace the spreadsheet.</h2>
        </div>
        <div className="pricing-grid">
          <div className="price-card">
            <h3>Starter</h3>
            <div className="price">$500<span>/mo</span></div>
            <ul>
              <li>Up to 5 seats</li>
              <li>100 RFQs / month</li>
              <li>Email import (simulated in Phase 1)</li>
              <li>PDF + CSV exports</li>
            </ul>
          </div>
          <div className="price-card feat">
            <div className="price-tag">Most popular</div>
            <h3>Professional</h3>
            <div className="price">$2,500<span>/mo</span></div>
            <ul>
              <li>Unlimited seats</li>
              <li>500+ RFQs / month</li>
              <li>API access</li>
              <li>Priority support</li>
            </ul>
          </div>
          <div className="price-card">
            <h3>Enterprise</h3>
            <div className="price">Talk to us</div>
            <ul>
              <li>Multi-location</li>
              <li>White-label</li>
              <li>Custom integrations</li>
              <li>SOC 2 Type II</li>
            </ul>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div>© Rivet · Aerospace Standard Parts RFQ Intelligence</div>
        <div className="landing-footer-powered">
          <img src="/cnerzy-logo.png" alt="cnerzy" style={{ width: 16, height: 16, borderRadius: 3, verticalAlign: 'middle', marginRight: 5 }} />
          <span>Powered by CNERZY</span>
        </div>
        <div><Link to="/signup">Start free</Link> · <Link to="/login">Sign in</Link></div>
      </footer>
    </div>
  );
}
